import { useScroll } from '@react-spring/web';
import cx from 'classnames';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FC,
  type MouseEvent,
} from 'react';

import { Icon } from 'src/components-v2/DataDisplay';
import { Btn } from 'src/components-v2/Inputs';
import { Column, Container, Row } from 'src/components-v2/Layout';
import PlaidLogo from 'src/components-v2/SVGs/PlaidLogo';
import { Link } from 'src/components/Inputs';
import { useDeviceSize } from 'src/hooks';

import { MobileNav } from './MobileNav';
import { NavList } from './NavList';

import { DEFAULT_THEME } from './consts';
import { navData } from './data';

import { useForeignContentNotification } from 'src/components/Feedback/ForeignContentNotification/ForeignContentNotification';
import { useMainTemplateContext } from 'src/contexts/MainTemplateContext/MainTemplateContext';
import { NEXT_PUBLIC_IS_PREVIEW, NEXT_PUBLIC_PERCY_RUN } from 'src/env';
import { isDevelopment, isTesting } from 'src/lib/env-constants';
import DevMenu from './DevMenu';
import { useNavLinkClickTracking } from './useNavLinkClickTracking';
import styles from './MainNavigationBar.module.scss';

type MainNavigationBarProps = {
  skipLinkAnchor?: string;
};

// show dev menu only in development or testing environment
const showDevMenu = isDevelopment || isTesting;

/** Web 3.0 header. All pages and locales should eventually use this. */
// A shared element lets React skip this icon when the nav re-renders right
// after hydration, before the icon's lazy chunk has loaded. A fresh element
// would update its Suspense boundary there and make React throw #421.
const MENU_ICON = <Icon icon='Menu' sx={{ width: 22, height: 22 }} />;

export const MainNavigationBar2: FC<MainNavigationBarProps> = ({
  skipLinkAnchor = '#main-content',
}) => {
  // COMMON
  const { isNewMainNavDesktop } = useDeviceSize();
  const { isForeignContentNotificationOpen } = useForeignContentNotification();
  const { isNavigationHidden } = useMainTemplateContext();
  const [isMounted, setIsMounted] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const handleNavLinkClick = useNavLinkClickTracking();

  const { items, ctaTrackingIds } = navData['en-us'];

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // SCROLL
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('up');
  const prevScrollY = useRef(0);

  const isVisible = !isNavigationHidden && scrollDirection === 'up';

  useScroll({
    onChange: ({ value: { scrollY } }) => {
      if (scrollY > 100) {
        setScrollDirection(scrollY > prevScrollY.current ? 'down' : 'up');
        prevScrollY.current = scrollY;
      }
    },
  });

  // MODAL
  const [isOpen, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (isOpen) {
      document.body.style.overflowY = 'hidden';
      document.body.style.height = 'initial';
    } else {
      document.body.style.overflowY = 'visible';
      document.body.style.height = '100%';
    }
  }, [isOpen]);

  // Update react state when native dialog is closed with escape key to keep body scroll in sync
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleNativeClose = () => {
      setOpen(false);
    };

    dialog.addEventListener('close', handleNativeClose);
    return () => {
      dialog.removeEventListener('close', handleNativeClose);
    };
  }, []);

  const handleOpenDialog = () => {
    if (dialogRef.current) {
      setOpen(true);
      dialogRef.current.showModal();
    }
  };

  const handleCloseDialog = useCallback(() => {
    dialogRef.current.close();
  }, []);

  // SMOOTH SCROLL
  const handleScrollTo = (event: MouseEvent<HTMLAnchorElement>) => {
    if (typeof document === 'undefined' || typeof window === 'undefined')
      return;

    const id = event.currentTarget.getAttribute('href');
    const tagetElement = document.querySelector(id) as HTMLElement;

    if (!tagetElement) return;

    event.preventDefault();

    const top = tagetElement.clientTop;
    const navHeight = navRef.current ? navRef.current.clientHeight : 0;

    window.scrollTo({
      top: top + navHeight,
      behavior: 'smooth',
    });

    // Focus
    const focusableElements = tagetElement.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );

    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  };

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <nav
        onClick={handleNavLinkClick}
        id='web3-nav'
        aria-label='Main Navigation'
        ref={navRef}
        className={cx(styles.Root, styles[`Root_${DEFAULT_THEME}`], {
          [styles['Root_show-by-scroll']]: isVisible,
          [styles['Root_hide-by-scroll']]: !isVisible,
          [styles['Root_foreign-content-notification-open']]:
            isForeignContentNotificationOpen &&
            !NEXT_PUBLIC_PERCY_RUN &&
            !NEXT_PUBLIC_IS_PREVIEW,
        })}
      >
        <Container className={styles.Root__container}>
          <Row>
            <Column xs={24} lgOffset={2} lg={20}>
              <div className={styles.Root__bar}>
                <div className={styles.Root__inner}>
                  <a
                    href={skipLinkAnchor}
                    className={styles['Root__link-to-content']}
                    onClick={handleScrollTo}
                  >
                    Skip to main content
                  </a>

                  {isMounted && isNewMainNavDesktop ? (
                    <>
                      <NavList items={items} isNavVisible={isVisible}>
                        <Link key={'qwer-23'} href='/'>
                          <a
                            aria-label='Go to Plaid homepage'
                            className={styles.Logo}
                          >
                            <PlaidLogo
                              sx={{
                                display: 'block',
                                height: { xs: '24px', sm: '28px' },
                                color: '#111',
                              }}
                            />
                          </a>
                        </Link>
                      </NavList>

                      <div className={styles.Root__toolkit}>
                        <Btn
                          className={cx(styles.Button, styles.Button_secondary)}
                          variant='secondary'
                          href='https://dashboard.plaid.com/'
                          tabIndex={0}
                        >
                          Log in
                        </Btn>

                        <Btn
                          className={cx(styles.Button, styles.Button_primary)}
                          variant='primary'
                          iconName='ChevronRight'
                          iconSize={16}
                          defaultGap='0.6rem'
                          href='/contact/'
                          trackingId={ctaTrackingIds.getAPIkeys}
                          tabIndex={0}
                        >
                          Contact sales
                        </Btn>
                      </div>
                    </>
                  ) : (
                    <>
                      <Link key={'qwer-56'} href='/'>
                        <a
                          className={styles.Logo}
                          aria-label='Go to Plaid homepage'
                        >
                          <PlaidLogo
                            sx={{
                              display: 'block',
                              height: { xs: '24px', sm: '28px' },
                              color: '#111',
                            }}
                          />
                        </a>
                      </Link>

                      <button
                        type='button'
                        className={styles.Burger}
                        tabIndex={0}
                        aria-label='Main menu'
                        aria-expanded={isOpen}
                        onClick={handleOpenDialog}
                      >
                        {MENU_ICON}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </Column>
          </Row>
        </Container>

        <MobileNav
          className={styles.Root__dialog}
          ref={dialogRef}
          items={items}
          onClose={handleCloseDialog}
          trackingIds={ctaTrackingIds}
        />
      </nav>
      {showDevMenu && <DevMenu />}
    </>
  );
};
